// ThumbnailCompositor.js
import Storage from './storage.js';

export default class ThumbnailCompositor {
    static async capture(selector, { scale = devicePixelRatio || 2, background = '#fff', grayscale = false } = {}) {
        const stack = typeof selector === 'string' ? document.querySelector(selector) : selector;
        if (!stack) throw new Error('stack not found');

        const stackRect = stack.getBoundingClientRect();
        const W = Math.max(1, Math.round(stackRect.width));
        const H = Math.max(1, Math.round(stackRect.height));

        const wallEl = stack.querySelector('.mscp-layer.wall');
        const shadowEl = stack.querySelector('.mscp-layer.shadow');
        const ppEl = stack.querySelector('.mscp-layer.pp');
        const frameEl = stack.querySelector('.mscp-layer.ram');
        const photoImg = stack.querySelector('img.mscp-layer.photo');
        const cropEl = stack.querySelector('.crop-container');

        const urls = {
            wall: this._bgUrl(wallEl),
            shadow: this._bgUrl(shadowEl) || '',
            pp: this._bgUrl(ppEl),
            frame: this._bgUrl(frameEl),
            photo: photoImg?.getAttribute('src') || null,
        };

        const [wall, shadow, pp, frame, photo] = await Promise.all([
            urls.wall ? this._loadImage(urls.wall) : null,
            urls.shadow ? this._loadImage(urls.shadow) : null,
            urls.pp ? this._loadImage(urls.pp) : null,
            urls.frame ? this._loadImage(urls.frame) : null,
            urls.photo ? this._loadImage(urls.photo) : null,
        ]);

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(W * scale);
        canvas.height = Math.round(H * scale);
        const ctx = canvas.getContext('2d', { alpha: background == null });

        if (background) {
            ctx.fillStyle = background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        const drawCover = (img) => { if (img) ctx.drawImage(img, 0, 0, canvas.width, canvas.height); };

        // bakgrundslager
        drawCover(wall);
        drawCover(shadow);

        // foto (clip till crop-container)
        if (photo && photoImg && cropEl) {
            const cropRect = cropEl.getBoundingClientRect();

            const clipX = Math.round((cropRect.left - stackRect.left) * scale);
            const clipY = Math.round((cropRect.top - stackRect.top) * scale);
            const clipW = Math.round(cropRect.width * scale);
            const clipH = Math.round(cropRect.height * scale);

            ctx.save();
            ctx.beginPath();
            ctx.rect(clipX, clipY, clipW, clipH);
            ctx.clip();

            const { tx, ty, sc } = this._parseTranslateScale(getComputedStyle(photoImg).transform);

            const dx = clipX + Math.round(tx * scale);
            const dy = clipY + Math.round(ty * scale);
            const dw = Math.round(photo.naturalWidth * sc * scale);
            const dh = Math.round(photo.naturalHeight * sc * scale);

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(photo, dx, dy, dw, dh);
            ctx.restore();
        }

        // pp ritas efter fotot (samma ordning som DOM/CSS där pp ligger ovanpå fotot)
        drawCover(pp);

        // drawCover(frame);

        // Apply grayscale if requested
        if (grayscale) {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < imageData.data.length; i += 4) {
                const r = imageData.data[i];
                const g = imageData.data[i + 1];
                const b = imageData.data[i + 2];
                // Simple average method
                const avg = Math.round((r + g + b) / 3);
                imageData.data[i] = avg;
                imageData.data[i + 1] = avg;
                imageData.data[i + 2] = avg;
            }
            ctx.putImageData(imageData, 0, 0);
        }

        return canvas;
    }

    static async captureToFile(selector, { scale = devicePixelRatio || 2, background = '#fff', grayscale = false } = {}) {
        const id = Storage?.get ? Storage.get('activeImageID') : 'tmp_' + Math.floor(Math.random() * 1000000);
        const unique = Date.now() + Math.floor(Math.random() * 10000);
        const filename = `${id}_thumb_${unique}.jpg`;

        // Accept grayscale in options
        const canvas = await this.capture(selector, { scale, background, grayscale: arguments[1]?.grayscale });
        const blob = await new Promise(res => canvas.toBlob(res, 'image/jpg'));
        return new File([blob], filename, { type: 'image/jpg' });
    }

    static async uploadImage(selector, { scale = devicePixelRatio || 2, background = '#fff', grayscale = false } = {}) {
        try {
            // Accept grayscale in options
            const file = await this.captureToFile(selector, { scale, background, grayscale: arguments[1]?.grayscale });
            const formData = new FormData();
            formData.append('file', file, file.name);

            const res = await fetch('/wp-json/mscp/v1/upload_thumb', {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                return { success: false, error: json?.message || `HTTP ${res.status}` };
            }
            if (json?.success && json?.url) {
                return { success: true, url: json.url };
            }
            return { success: false, error: json?.message || 'Unknown server error' };
        } catch (err) {
            return { success: false, error: err?.message || String(err) };
        }
    }

    // --- helpers ---
    static _bgUrl(el) {
        if (!el) return null;
        const m = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(el).backgroundImage || '');
        return m ? m[1] : null;
    }

    static async _loadImage(url) {
        if (url == null) return null;
        if (url === '') return null; // empty url, treat as no image

        const res = await fetch(url, { credentials: 'include' });
        if (res.status === 204) return null; // no content, treat as no image
        if (!res.ok) return null; // treat fetch errors as no image
        // if (!res.ok) throw new Error(`Fetch ${res.status}: ${url}`);
        const blob = await res.blob();
        const obj = URL.createObjectURL(blob);
        try {
            const img = await new Promise((resolve, reject) => {
                const i = new Image();
                i.onload = () => resolve(i);
                i.onerror = reject;
                i.src = obj;
            });
            return img;
        } finally {
            // URL.revokeObjectURL(obj); // kan köras efter draw om du vill
        }
    }

    static _parseTranslateScale(transform) {
        if (!transform || transform === 'none') return { tx: 0, ty: 0, sc: 1 };
        const mm = /matrix\(([^)]+)\)/.exec(transform);
        if (mm) {
            const [a, , , , tx, ty] = mm[1].split(',').map(n => parseFloat(n.trim()));
            return { tx, ty, sc: a || 1 };
        }
        const t = /translate\(\s*([-\d.]+)px(?:\s*,\s*([-\d.]+)px)?\s*\)/.exec(transform) || [];
        const s = /scale\(\s*([-\d.]+)\s*\)/.exec(transform) || [];
        return { tx: parseFloat(t[1] || '0'), ty: parseFloat(t[2] || '0'), sc: parseFloat(s[1] || '1') };
    }
}